import React, { useState, useEffect, useCallback } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Users, Search, RefreshCw, Trash2, AlertTriangle } from 'lucide-react';

/**
 * Admin-Tab: kompakte Userübersicht (Aug 2026).
 * ivoliq/lex-Trennung, Plan, Registrierungsdatum, Onboarding-Status + Suche.
 */

const PLAN_COLORS = {
  free: 'bg-gray-100 text-gray-600',
  basic: 'bg-sky-100 text-sky-700',
  pro: 'bg-emerald-100 text-emerald-800',
};

function planBadge(u) {
  const plan = (u.plan || 'free').toLowerCase();
  const key = plan.startsWith('pro') ? 'pro' : plan.startsWith('basic') ? 'basic' : 'free';
  return (
    <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${PLAN_COLORS[key]}`}>
      {u.plan}{u.has_lifetime_access ? ' · LT' : ''}
    </span>
  );
}

export default function AdminUserOverviewPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [variant, setVariant] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async (searchTerm, variantFilter) => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (searchTerm) params.search = searchTerm;
      if (variantFilter && variantFilter !== 'all') params.variant = variantFilter;
      const res = await api.get('/admin/users', { params });
      setData(res.data);
    } catch (e) {
      console.error('Userübersicht laden fehlgeschlagen:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(search, variant); }, [variant, load]);

  const onSearch = (e) => {
    e.preventDefault();
    load(search, variant);
  };

  const users = data?.users || [];

  const openDelete = (u) => {
    setDeleteTarget(u);
    setConfirmEmail('');
    setDeleteReason('');
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await api.post(`/admin/users/${deleteTarget.id}/delete`, {
        confirm_email: confirmEmail.trim(),
        reason: deleteReason.trim() || undefined,
      });
      toast.success(`Konto ${res.data.email} DSGVO-konform gelöscht (${res.data.deleted_docs} Dokumente)${res.data.email_sent ? ' — Bestätigungsmail versendet' : ' — Mail-Versand fehlgeschlagen!'}`);
      setDeleteTarget(null);
      load(search, variant);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Konto-Löschung fehlgeschlagen');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-user-overview">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="h-6 w-6 text-emerald-600" />
            Userübersicht
          </h2>
          <p className="text-gray-600 mt-1">
            Alle registrierten Nutzer — {data ? <>ivoliq: <b data-testid="user-overview-total-ivoliq">{data.total_ivoliq}</b> · lex: <b data-testid="user-overview-total-lex">{data.total_lex}</b></> : '…'}
          </p>
        </div>
        <button onClick={() => load(search, variant)} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800" data-testid="user-overview-refresh">
          <RefreshCw className="h-4 w-4" /> Aktualisieren
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={onSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="E-Mail oder Name suchen…"
              className="border border-gray-300 rounded-md pl-8 pr-3 py-2 text-sm w-64"
              data-testid="user-overview-search" />
          </div>
          <button type="submit" className="text-sm px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-testid="user-overview-search-btn">Suchen</button>
        </form>
        <div className="flex rounded-md border border-gray-300 overflow-hidden text-sm" data-testid="user-overview-variant-filter">
          {[['all', 'Alle'], ['ivoliq', 'ivoliq'], ['lex', 'lex']].map(([v, label]) => (
            <button key={v} onClick={() => setVariant(v)}
              className={`px-3 py-2 ${variant === v ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              data-testid={`user-overview-variant-${v}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="pt-5 overflow-x-auto">
          {loading && !data ? (
            <p className="text-sm text-gray-500 py-4 text-center" data-testid="user-overview-loading">Lade Nutzer…</p>
          ) : users.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center" data-testid="user-overview-empty">Keine Nutzer gefunden.</p>
          ) : (
            <table className="w-full text-sm" data-testid="user-overview-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-3">E-Mail</th>
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Produkt</th>
                  <th className="py-2 pr-3">Plan</th>
                  <th className="py-2 pr-3">Land</th>
                  <th className="py-2 pr-3">Registriert</th>
                  <th className="py-2 pr-3">Onboarding</th>
                  <th className="py-2 pr-3">Verifiziert</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-gray-100" data-testid={`user-overview-row-${u.email}`}>
                    <td className="py-1.5 pr-3 text-gray-900">{u.email}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{u.name || '—'}</td>
                    <td className="py-1.5 pr-3">
                      {u.account_variant === 'lex'
                        ? <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded text-white" style={{ backgroundColor: '#0A1128' }}>lex</span>
                        : <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-50 text-emerald-700 border border-emerald-200">ivoliq</span>}
                    </td>
                    <td className="py-1.5 pr-3">{planBadge(u)}</td>
                    <td className="py-1.5 pr-3 text-gray-500 text-xs">{u.country || '—'}</td>
                    <td className="py-1.5 pr-3 text-gray-500 text-xs">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('de-DE') : '—'}
                    </td>
                    <td className="py-1.5 pr-3">{u.onboarding_completed ? <span className="text-emerald-600">✓</span> : <span className="text-gray-300">—</span>}</td>
                    <td className="py-1.5 pr-3">{u.email_verified ? <span className="text-emerald-600">✓</span> : <span className="text-gray-300">—</span>}</td>
                    <td className="py-1.5 text-right">
                      <button onClick={() => openDelete(u)} title="Konto DSGVO-konform löschen"
                        className="p-1.5 rounded text-gray-300 hover:text-red-600 hover:bg-red-50"
                        data-testid={`user-delete-btn-${u.email}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      {data && <p className="text-xs text-gray-400">{data.count} angezeigt (max. 300 — Suche zum Eingrenzen nutzen).</p>}

      {/* DSGVO-Lösch-Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="user-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Konto unwiderruflich löschen
            </DialogTitle>
            <DialogDescription>
              <b>{deleteTarget?.email}</b> ({deleteTarget?.account_variant}) wird DSGVO-konform gelöscht:
              alle Nutzerdaten (Rechnungen, Belege, Zeiten, Kunden …) werden entfernt, ein aktives
              Stripe-Abo wird gekündigt und der Nutzer erhält eine Bestätigungsmail über die Löschung.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Zur Bestätigung E-Mail des Kontos eintippen</label>
              <input value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder={deleteTarget?.email}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                data-testid="user-delete-confirm-email" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Grund (optional, anonymisierte Statistik)</label>
              <input value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="z. B. Löschantrag per E-Mail vom 12.06."
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                data-testid="user-delete-reason" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50" data-testid="user-delete-cancel">
              Abbrechen
            </button>
            <button onClick={doDelete}
              disabled={deleting || confirmEmail.trim().toLowerCase() !== (deleteTarget?.email || '').toLowerCase()}
              className="px-4 py-2 text-sm font-semibold rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="user-delete-confirm">
              {deleting ? 'Lösche …' : 'Endgültig löschen'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
